/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography, Button } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { IoCopyOutline } from 'react-icons/io5';

import useStyles from './styles';
import Default from '../../contenedores/Default';
import TextField from '../../componentes/TextField';
import CustomSelect from '../../componentes/Select';
import EditorContrato from '../../componentes/EditorContrato';
import ModalClonarPlantilla from './ModalClonarPlantilla';
import { ConfiguracionXMLHttp, peticionXMLHttp } from '../../utilidades/peticionXMLHttp';

import endpoints, { PLANTILLAS_CONTRATO } from '../../configuraciones/endpoints';
import axios from '../../configuraciones/axios';
import estatus from '../../constantes/estatus';
import { TIPOS_PLANTILLA_CONTRATO } from '../../constantes/variablesContrato';
import { CAMPO_REQUERIDO, ELEMENTO_NO_ENCONTRADO } from '../../constantes/mensajes';
import { trim } from '../../utilidades/functions';
import sanitizeContractHtml from '../../componentes/EditorContrato/utilidades/sanitizer';

const schema = yup.object({
  nombre: yup.string().required(CAMPO_REQUERIDO),
  tipo: yup.string().required(CAMPO_REQUERIDO),
  activo: yup.boolean().required(CAMPO_REQUERIDO),
  contenido: yup.string().required('El contenido del machote es requerido'),
});

const PlantillaContrato = ({ history, match }) => {
  const { params: { id } } = match;
  const classes = useStyles();
  const token = useSelector(({ user }) => user?.token);

  const [modalClonarAbierto, setModalClonarAbierto] = useState(false);
  const [descargandoVistaPrevia, setDescargandoVistaPrevia] = useState(false);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nombre: '',
      tipo: 'CREDITO',
      activo: true,
      contenido: '',
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    register,
    formState: { errors },
  } = methods;

  useEffect(() => {
    register('contenido');
  }, [register]);

  const contenido = watch('contenido');
  const tipo = watch('tipo');

  const regresar = () => {
    const { location: { state } } = history;
    const pagina = state?.pagina;
    history.push({
      pathname: '/catalogos/plantillas-contrato',
      search: pagina && pagina > 1 ? `?pagina=${pagina}` : '',
    });
  };

  const consultarDatosIniciales = () =>
    new Promise((resolve, reject) => {
      if (id) {
        axios
          .get(endpoints.base.url(PLANTILLAS_CONTRATO, id))
          .then((plantillaDb) => {
            if (plantillaDb?.id) {
              reset({
                nombre: plantillaDb.nombre,
                tipo: plantillaDb.tipo,
                activo: plantillaDb.activo,
                contenido: sanitizeContractHtml(plantillaDb.contenido || ''),
              });
            } else {
              toast.warning(ELEMENTO_NO_ENCONTRADO);
              regresar();
            }
            resolve();
          })
          .catch((err) => {
            toast.error('Error al cargar la plantilla');
            regresar();
            reject(err);
          });
      } else {
        resolve();
      }
    });

  useEffect(() => {
    consultarDatosIniciales();
  }, [id]);

  const cancelar = useCallback(regresar, []);

  const guardar = useCallback(
    (datos) => {
      const metodo = id ? 'put' : 'post';
      const datosSanitizados = {
        ...trim(datos),
        contenido: sanitizeContractHtml(datos?.contenido || ''),
      };
      axios[metodo](endpoints.base.url(PLANTILLAS_CONTRATO, id), datosSanitizados)
        .then(() => {
          toast.success(id ? 'Plantilla actualizada exitosamente' : 'Plantilla creada exitosamente');
          regresar();
        })
        .catch(() => {});
    },
    [id]
  );

  const handleEditorChange = useCallback(
    (nuevoHtml) => {
      setValue('contenido', nuevoHtml, { shouldValidate: true });
    },
    [setValue]
  );

  const handleClonar = useCallback(
    ({ contenido: nuevoContenido, tipo: nuevoTipo }) => {
      if (nuevoTipo && nuevoTipo !== watch('tipo')) {
        setValue('tipo', nuevoTipo, { shouldValidate: true, shouldDirty: true });
      }
      setValue('contenido', nuevoContenido, {
        shouldValidate: true,
        shouldDirty: true,
      });
      toast.info(
        'Se ha clonado el contenido de la plantilla en el editor. Puede editar los datos necesarios y guardar cuando lo decida.'
      );
    },
    [setValue, watch]
  );

  const descargarVistaPrevia = useCallback(async () => {
    const contenidoActual = watch('contenido');
    if (!contenidoActual || !contenidoActual.trim() || contenidoActual === '<p><br></p>') {
      toast.warning('El editor está vacío. Ingrese texto o variables para descargar la vista previa.');
      return;
    }

    try {
      setDescargandoVistaPrevia(true);
      const nombrePlantilla = watch('nombre') || 'Plantilla_Contrato';
      const tipoPlantilla = watch('tipo') || 'CREDITO';

      const nombreLimpio =
        nombrePlantilla
          .replace(/[^a-zA-Z0-9_\-\s]/g, '')
          .trim()
          .replace(/\s+/g, '_')
          .substring(0, 50) || 'Contrato';

      const configuracion = new ConfiguracionXMLHttp({
        tipoPeticion: 'POST',
        token,
        body: {
          contenido: contenidoActual,
          nombre: nombrePlantilla,
          tipo: tipoPlantilla,
        },
        formatoArchivo: 'word',
        urlEndpoint: endpoints.descargarVistaPreviaPlantilla(),
        nombreArchivo: `VISTA_PREVIA_${nombreLimpio}`,
      });

      await peticionXMLHttp(configuracion);
      toast.success('Documento de vista previa descargado exitosamente');
    } catch (err) {
      // Manejado automáticamente por peticionXMLHttp
    } finally {
      setDescargandoVistaPrevia(false);
    }
  }, [watch, token]);

  return (
    <Default
      titulo={Boolean(id) ? 'Editar plantilla de contrato' : 'Nueva plantilla de contrato'}
      mostrarCabeceroFormulario
      cancelar={cancelar}
      frmId="formPlantillaContrato"
    >
      <FormProvider {...methods}>
        <form id="formPlantillaContrato" onSubmit={handleSubmit(guardar)}>
          <Grid container className={classes.generales} spacing={2}>
            {/* Metadatos de la plantilla */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre del machote: *"
                name="nombre"
                placeHolder="Ej. Contrato de Crédito 2026"
                inputProps={{ maxLength: 150 }}
                error={errors.nombre}
                helperText={errors.nombre?.message}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomSelect
                label="Tipo de Contrato: *"
                name="tipo"
                placeHolder="Seleccione el tipo"
                options={TIPOS_PLANTILLA_CONTRATO}
                error={errors.tipo}
                helperText={errors.tipo?.message}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <CustomSelect
                label="Estatus: *"
                name="activo"
                placeHolder="Estatus"
                options={estatus}
                error={errors.activo}
                helperText={errors.activo?.message}
              />
            </Grid>

            {/* Editor de Contrato con Botón de Clonar */}
            <Grid item xs={12} className={classes.editorSection}>
              <div className={classes.editorHeader}>
                <Typography className={classes.editorLabel}>
                  Redacción del Contrato: *
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setModalClonarAbierto(true)}
                  className={classes.btnClonarPrincipal}
                  startIcon={<IoCopyOutline />}
                >
                  Clonar desde otra plantilla
                </Button>
              </div>
              <Typography className={classes.ayudaTexto}>
                Escriba el texto del contrato con el formato deseado. Utilice el botón <b>"Insertar Variable"</b> en la barra superior para colocar los datos dinámicos que se sustituirán al generar cada contrato. Puede pulsar <b>"Descargar vista previa"</b> para validar el formato resultante en Word con información de prueba y marca de agua.
              </Typography>
              <EditorContrato
                value={contenido}
                onChange={handleEditorChange}
                placeholder="Escriba aquí las declaraciones, cláusulas y cuerpo del contrato..."
                onDescargarVistaPrevia={descargarVistaPrevia}
                descargandoVistaPrevia={descargandoVistaPrevia}
              />
              {errors.contenido && (
                <Typography className={classes.errorText}>
                  {errors.contenido?.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      {/* Modal independiente con todo el flujo de clonación */}
      <ModalClonarPlantilla
        open={modalClonarAbierto}
        onClose={() => setModalClonarAbierto(false)}
        tipoActual={tipo}
        plantillaActualId={id}
        onClonar={handleClonar}
      />
    </Default>
  );
};

export default React.memo(PlantillaContrato);
